import React, { useEffect, useState, type FormEvent } from "react";
import { api, ApiError, setTokenGetter } from "../api";
import { CurrentCaseResponse } from "../types";
import { Alert, Spinner } from "../common/ui";
import { Dashboard } from "./Dashboard";
import { Wizard } from "./Wizard";

const TOKEN_KEY = "rz_merchant_token";

export function MerchantApp() {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [data, setData] = useState<CurrentCaseResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<"dashboard" | "wizard">("dashboard");

  // Set synchronously during render, not in useEffect — see AdminApp.tsx
  // for why an effect-based getter can race a child's own mount effect.
  setTokenGetter(() => token);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.getCurrentCase();
      setData(d);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        logout();
      } else {
        setError((e as Error).message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setData(null);
  }

  function handleLoggedIn(newToken: string) {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
  }

  if (!token) {
    return <LoginScreen onLoggedIn={handleLoggedIn} />;
  }

  return (
    <div className="mobile-shell">
      <div className="topbar">
        <div className="brand">Raymand <span className="accent">×</span> Zoodex</div>
        <button className="btn btn-ghost btn-sm" style={{ color: "#fff" }} onClick={logout}>خروج</button>
      </div>
      {loading && !data && (
        <div className="wizard-body" style={{ textAlign: "center", paddingTop: 60 }}>
          <Spinner /> در حال بارگذاری...
        </div>
      )}
      {error && (
        <div className="wizard-body">
          <Alert kind="error">{error}</Alert>
          <button className="btn btn-secondary btn-block" onClick={refresh}>تلاش مجدد</button>
        </div>
      )}
      {data && view === "dashboard" && (
        <Dashboard data={data} onContinue={() => setView("wizard")} onRefresh={refresh} />
      )}
      {data && view === "wizard" && (
        <Wizard data={data} onRefresh={refresh} onExit={() => setView("dashboard")} />
      )}
    </div>
  );
}

function LoginScreen({ onLoggedIn }: { onLoggedIn: (token: string) => void }) {
  const [stage, setStage] = useState<"identify" | "otp">("identify");
  const [zoodexId, setZoodexId] = useState("");
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function requestOtp(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.requestOtp(zoodexId.trim(), mobile.trim());
      setDevCode(res.devCode || null);
      setStage("otp");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.verifyOtp(zoodexId.trim(), mobile.trim(), code.trim());
      onLoggedIn(res.token);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="center-screen">
      <div className="card login-card">
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div className="card-title" style={{ fontSize: 18 }}>سرویس تأمین مالی Raymand × Zoodex</div>
          <p>تسهیلات سرمایه در گردش بانک صادرات، ویژه پذیرندگان زودکس</p>
        </div>
        {error && <Alert kind="error">{error}</Alert>}
        {stage === "identify" && (
          <form onSubmit={requestOtp}>
            <div className="field">
              <label>شناسه پذیرنده زودکس (Zoodex Merchant ID)</label>
              <input type="text" placeholder="مثلاً ZD-10234" value={zoodexId} onChange={(e: any) => setZoodexId(e.target.value)} required />
            </div>
            <div className="field">
              <label>شماره موبایل صاحب جواز</label>
              <input type="tel" placeholder="09xxxxxxxxx" value={mobile} onChange={(e: any) => setMobile(e.target.value)} required />
            </div>
            <button className="btn btn-primary" disabled={loading}>{loading ? <Spinner /> : "دریافت کد تأیید"}</button>
            <p className="text-sm mt-3" style={{ textAlign: "center" }}>
              نمونه برای تست: ZD-10234 / 09120000001
            </p>
          </form>
        )}
        {stage === "otp" && (
          <form onSubmit={verifyOtp}>
            <p style={{ textAlign: "center" }}>کد ۶ رقمی ارسال شده به {mobile} را وارد کنید.</p>
            {devCode && <Alert kind="info">کد آزمایشی (محیط توسعه): <b style={{ fontFamily: "monospace" }}>{devCode}</b></Alert>}
            <div className="field">
              <input className="otp-input" inputMode="numeric" maxLength={6} value={code} onChange={(e: any) => setCode(e.target.value)} required />
            </div>
            <button className="btn btn-primary" disabled={loading}>{loading ? <Spinner /> : "تأیید و ورود"}</button>
            <button type="button" className="btn btn-ghost btn-block mt-2" onClick={() => setStage("identify")}>بازگشت</button>
          </form>
        )}
      </div>
    </div>
  );
}
